import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Pagination,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../../utils/Api";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import { CloseOutlined } from "@mui/icons-material";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";

const DamagedRawMaterials = () => {
  const token = localStorage.getItem("token");
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [allRawMaterials, setAllRawMaterials] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [remark, setRemark] = useState("");
  const [quantities, setQuantities] = useState({});
  const [attachments, setAttachments] = useState({});

  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const fetchAllRawMaterials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { outletId: outletData?._id, limit: 1000 },
        }
      );
      setAllRawMaterials(response?.data?.data?.totalRecords || []);
      setCategories(response?.data?.data?.rawMaterialCategories || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch raw materials.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllRawMaterials();
  }, []);

  const uniqueCategories = useMemo(() => {
    const cats = Array.isArray(categories) ? categories : [];
    return cats.filter(
      (category, index, self) =>
        index ===
        self.findIndex((c) => c?.mainCategory?._id === category?.mainCategory?._id)
    );
  }, [categories]);

  useEffect(() => {
    let filtered = [...(Array.isArray(allRawMaterials) ? allRawMaterials : [])];

    if (selectedCategory) {
      filtered = filtered.filter(
        (item) => item?.rawMaterial?.mainCategory?._id === selectedCategory
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((item) =>
        (item?.rawMaterial?.name || "").toLowerCase().includes(q)
      );
    }

    setRawMaterials(filtered);
    setCurrentPage(1);
  }, [allRawMaterials, selectedCategory, searchQuery]);

  const hasSearch = !!searchQuery.trim();
  const hasResults = rawMaterials.length > 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMaterials = rawMaterials.slice(startIndex, endIndex);

  const handlePageChange = (event, page) => setCurrentPage(page);

  const handleQuantityChange = (itemId, inputValue, availableStock) => {
    const numericValue = parseFloat(inputValue);
    const maxStock = Number(availableStock || 0);

    if (!inputValue) {
      setQuantities((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });

      setSelectedItems((prev) => prev.filter((id) => id !== itemId));
      return;
    }

    if (isNaN(numericValue) || numericValue <= 0) {
      return;
    }

    if (numericValue > maxStock) {
      toast.error(`Damage quantity cannot exceed available stock (${maxStock}).`);
      return;
    }

    setQuantities((prev) => ({
      ...prev,
      [itemId]: inputValue,
    }));

    setSelectedItems((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
  };

  const handleAttachmentChange = (itemId, event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) return;

    const allowedImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    const validFiles = files.filter((file) => allowedImageTypes.includes(file.type));

    if (validFiles.length !== files.length) {
      toast.error("Only JPG, JPEG, PNG and WEBP images are allowed.");
    }

    setAttachments((prev) => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...validFiles],
    }));

    event.target.value = "";
  };

  const removeAttachment = (itemId, fileIndex) => {
    setAttachments((prev) => {
      const existingFiles = prev[itemId] || [];
      const updatedFiles = existingFiles.filter((_, index) => index !== fileIndex);

      const updated = { ...prev };
      if (updatedFiles.length > 0) {
        updated[itemId] = updatedFiles;
      } else {
        delete updated[itemId];
      }

      return updated;
    });
  };

  const removeSelectedItem = (itemId) => {
    setSelectedItems((prev) => prev.filter((id) => id !== itemId));

    setQuantities((prev) => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });

    setAttachments((prev) => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
  };

  const resetForm = () => {
    setSelectedItems([]);
    setQuantities({});
    setAttachments({});
    setRemark("");
    setSearchQuery("");
    setSelectedCategory("");
    setCurrentPage(1);
  };

  const handleSubmit = async () => {
    if (!selectedItems.length) {
      toast.error("Please select at least one item by adding quantities.");
      return;
    }

    if (!remark.trim()) {
      toast.error("Please provide a reason for the transaction.");
      return;
    }

    const itemWithoutImage = selectedItems.find(
      (itemId) => !attachments[itemId] || attachments[itemId].length === 0
    );

    if (itemWithoutImage) {
      const selectedItem = allRawMaterials.find(
        (i) => i?.rawMaterial?._id === itemWithoutImage
      );

      toast.error(
        `Please upload at least one image for ${selectedItem?.rawMaterial?.name || "each selected raw material"
        }.`
      );
      return;
    }

    const formData = new FormData();
    formData.append("outletId", outletData?._id || "");
    formData.append("remark", remark);

    selectedItems.forEach((itemId, index) => {
      formData.append(
        `outletStockTransferData[${index}][rawMaterialId]`,
        itemId
      );
      formData.append(
        `outletStockTransferData[${index}][transferQuantity]`,
        Number(quantities[itemId])
      );

      const itemFiles = attachments[itemId] || [];
      itemFiles.forEach((file) => {
        formData.append(
          `outletStockTransferData[${index}][attachments]`,
          file
        );
      });
    });

    try {
      setSubmitting(true);

      const response = await axios.post(
        `${API.BASE_URL}stock-transactions/debitDamageStockFromOutlet`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data) {
        toast.success("Damaged raw materials logged successfully.");
        resetForm();
        fetchAllRawMaterials();
      } else {
        throw new Error("Submission failed.");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to log damaged raw materials."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = selectedItems?.length || 0;

  return (
    <Box className="dashBoard" sx={{ minHeight: "100vh" }}>
      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  background: "#fff",
                }}
              >
                <CardContent sx={{ py: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#334155",
                          mb: 0.8,
                        }}
                      >
                        Search Raw Material
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Type to search raw materials..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchRounded />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#334155",
                          mb: 0.8,
                        }}
                      >
                        Category
                      </Typography>
                      <FormControl
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                          },
                        }}
                      >
                        <Select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>All</em>
                          </MenuItem>
                          {uniqueCategories?.map((category) => (
                            <MenuItem
                              key={category?.mainCategory?._id}
                              value={category?.mainCategory?._id}
                            >
                              {category?.mainCategory?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {loading ? (
                    <Box
                      sx={{
                        py: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                      }}
                    >
                      <CircularProgress />
                      <Typography sx={{ color: "#334155", fontWeight: 700 }}>
                        Fetching raw materials...
                      </Typography>
                    </Box>
                  ) : !hasSearch && !selectedCategory ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: "#0f172a",
                        }}
                      >
                        Start by searching above
                      </Typography>
                      <Typography
                        sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}
                      >
                        Type at least 1 character to see matching raw materials.
                      </Typography>
                    </Box>
                  ) : paginatedMaterials?.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: "#0f172a",
                        }}
                      >
                        No raw materials found
                      </Typography>
                      <Typography
                        sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}
                      >
                        No matches for “{searchQuery || "selected filter"}”.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderBottom: "1px solid #e6eaf0",
                          background: "#fff",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                          alignItems="center"
                        >
                          <Chip
                            icon={<FilterAltOutlined />}
                            label={`Rows: ${rawMaterials.length}`}
                            variant="outlined"
                            sx={{ fontWeight: 800, borderRadius: 2 }}
                          />
                          <Chip
                            label={`Page: ${currentPage} / ${Math.max(
                              1,
                              Math.ceil(rawMaterials.length / itemsPerPage)
                            )}`}
                            variant="outlined"
                            sx={{ fontWeight: 800, borderRadius: 2 }}
                          />
                        </Stack>
                      </Box>

                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          borderRadius: 0,
                          maxHeight: "62vh",
                          overflow: "auto",
                        }}
                      >
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              {["Raw Material", "Available", "Damage Qty"].map((h) => (
                                <TableCell
                                  key={h}
                                  sx={{
                                    fontWeight: 900,
                                    color: "#0f172a",
                                    background: "#f1f5f9",
                                    borderBottom: "1px solid #e2e8f0",
                                    whiteSpace: "nowrap",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                  }}
                                  align={h === "Raw Material" ? "left" : "center"}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {paginatedMaterials.map((item, index) => {
                              const itemId = item?.rawMaterial?._id;
                              const isSelected = selectedItems.includes(itemId);
                              const isOutOfStock =
                                Number(item?.currentStock || 0) <= 0;

                              return (
                                <TableRow
                                  key={itemId || index}
                                  hover
                                  sx={{
                                    backgroundColor: isSelected
                                      ? "#f8fbff"
                                      : index % 2 === 0
                                        ? "#ffffff"
                                        : "#EDF9FF",
                                    "& td": {
                                      borderBottom: "1px solid #f1f5f9",
                                    },
                                  }}
                                >
                                  <TableCell align="left">
                                    <Typography
                                      sx={{
                                        fontSize: 14,
                                        fontWeight: 900,
                                        color: "#0f172a",
                                        textTransform: "capitalize",
                                      }}
                                    >
                                      {item?.rawMaterial?.name || "N/A"}
                                    </Typography>

                                    <Typography
                                      sx={{ fontSize: 12, color: "#64748b" }}
                                    >
                                      {item?.rawMaterial?.mainCategory?.name || "—"} •{" "}
                                      {item?.rawMaterial?.unit || "—"}
                                    </Typography>
                                  </TableCell>

                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontWeight: 800,
                                      color: isOutOfStock ? "#b91c1c" : "#0f172a",
                                    }}
                                  >
                                    {Number(item?.currentStock || 0).toFixed(2)}{" "}
                                    {item?.rawMaterial?.unit}
                                    {isOutOfStock ? (
                                      <Box sx={{ mt: 0.6 }}>
                                        <Chip
                                          label="Out of Stock"
                                          size="small"
                                          sx={{
                                            fontWeight: 900,
                                            borderRadius: 2,
                                            backgroundColor: "#fdecea",
                                            color: "#b91c1c",
                                            "& .MuiChip-label": {
                                              color: "#b91c1c",
                                            },
                                          }}
                                        />
                                      </Box>
                                    ) : null}
                                  </TableCell>

                                  <TableCell align="center">
                                    {isOutOfStock ? (
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          fontWeight: 800,
                                          color: "#94a3b8",
                                        }}
                                      >
                                        —
                                      </Typography>
                                    ) : (
                                      <TextField
                                        size="small"
                                        placeholder="Qty"
                                        value={quantities[itemId] || ""}
                                        onChange={(e) =>
                                          handleQuantityChange(
                                            itemId,
                                            e.target.value,
                                            item?.currentStock
                                          )
                                        }
                                        onKeyDown={(e) => {
                                          const allowed = [
                                            "Backspace",
                                            "Tab",
                                            "ArrowLeft",
                                            "ArrowRight",
                                            "Delete",
                                            "Home",
                                            "End",
                                          ];
                                          if (allowed.includes(e.key)) return;
                                          if (!/[0-9.]/.test(e.key)) {
                                            e.preventDefault();
                                          }
                                        }}
                                        sx={{
                                          width: 120,
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor: "#fff",
                                            height: 40,
                                            "&.Mui-focused fieldset": {
                                              borderColor: "#003049",
                                            },
                                          },
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Divider />

                      {hasResults && (
                        <Box
                          sx={{
                            py: 2,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Pagination
                            count={Math.ceil(rawMaterials.length / itemsPerPage)}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            variant="outlined"
                            shape="rounded"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  background: "#fff",
                }}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      <Typography
                        sx={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}
                      >
                        Selected Items
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: "#64748b", mt: 0.3 }}
                      >
                        Add quantity and upload damage images for each selected item.
                      </Typography>
                    </Box>

                    <Chip
                      icon={<FilterAltOutlined />}
                      label={`${selectedCount} Selected`}
                      variant="outlined"
                      sx={{ fontWeight: 800, borderRadius: 2 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  {selectedCount === 0 ? (
                    <Box sx={{ py: 3, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                        No items selected yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {selectedItems.map((itemId) => {
                        const selectedItem = allRawMaterials.find(
                          (i) => i?.rawMaterial?._id === itemId
                        );

                        if (!selectedItem) return null;

                        const itemFiles = attachments[itemId] || [];

                        return (
                          <Box
                            key={itemId}
                            sx={{
                              border: "1px solid #e6eaf0",
                              borderRadius: 2,
                              px: 1.5,
                              py: 1.5,
                              background: "#f8fbff",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 1.5,
                              }}
                            >
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 900,
                                    color: "#0f172a",
                                    textTransform: "capitalize",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {selectedItem?.rawMaterial?.name || "N/A"}
                                </Typography>

                                <Typography
                                  sx={{ fontSize: 12, color: "#64748b", mt: 0.4 }}
                                >
                                  Qty: <b>{quantities[itemId]}</b>{" "}
                                  {selectedItem?.rawMaterial?.unit}
                                </Typography>

                                <Typography
                                  sx={{ fontSize: 12, color: "#64748b", mt: 0.2 }}
                                >
                                  Available:{" "}
                                  <b>
                                    {Number(
                                      selectedItem?.currentStock || 0
                                    ).toFixed(2)}
                                  </b>{" "}
                                  {selectedItem?.rawMaterial?.unit}
                                </Typography>
                              </Box>

                              <IconButton
                                onClick={() => removeSelectedItem(itemId)}
                                sx={{
                                  color: "#0f172a",
                                  border: "1px solid #e6eaf0",
                                  borderRadius: 2,
                                  "&:hover": { background: "#fff" },
                                }}
                                size="small"
                              >
                                <CloseOutlined fontSize="small" />
                              </IconButton>
                            </Box>

                            <Box sx={{ mt: 1.5 }}>
                              <input
                                id={`damage-images-${itemId}`}
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={(e) => handleAttachmentChange(itemId, e)}
                              />

                              <label htmlFor={`damage-images-${itemId}`}>
                                <Button
                                  component="span"
                                  variant="outlined"
                                  startIcon={<CloudUploadOutlined />}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 800,
                                  }}
                                >
                                  Upload Images
                                </Button>
                              </label>
                            </Box>

                            {itemFiles.length > 0 && (
                              <Stack spacing={1} sx={{ mt: 1.5 }}>
                                {itemFiles.map((file, fileIndex) => (
                                  <Box
                                    key={`${file.name}-${fileIndex}`}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                      border: "1px solid #dbeafe",
                                      background: "#fff",
                                      borderRadius: 2,
                                      px: 1.2,
                                      py: 0.8,
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ minWidth: 0, flex: 1 }}
                                    >
                                      <ImageOutlined
                                        sx={{
                                          fontSize: 18,
                                          color: "#2563eb",
                                          flexShrink: 0,
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          color: "#334155",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {file.name}
                                      </Typography>
                                    </Stack>

                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        removeAttachment(itemId, fileIndex)
                                      }
                                      sx={{
                                        color: "#b91c1c",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <DeleteOutlineOutlined fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Stack>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Card>

              <Card
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  background: "#fff",
                }}
              >
                <CardContent>
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}
                  >
                    Reason for Damage / Wastage
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter reason (required)"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "#fff",
                      },
                    }}
                  />
                </CardContent>
              </Card>

              <Card
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  border: "1px solid #e6eaf0",
                  background: "#fff",
                }}
              >
                <CardContent>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={!selectedItems.length || submitting || loading}
                    sx={{
                      height: 52,
                      borderRadius: 2,
                      fontWeight: 900,
                      textTransform: "none",
                      backgroundColor: "#003049",
                      "&:hover": { backgroundColor: "#00263a" },
                      "&.Mui-disabled": {
                        backgroundColor: "#b0bec5",
                        color: "#ffffff",
                      },
                    }}
                  >
                    {submitting ? "Processing..." : "Confirm Damage"}
                  </Button>

                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#64748b",
                      mt: 1,
                      textAlign: "center",
                    }}
                  >
                    Quantities and uploaded images will be sent in multipart form-data.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DamagedRawMaterials;